// src/utils/slotStory.js
//
// Slot Story (docs/slot-story-spec.md Contract C). Pure functions that turn ONE
// server/local lifecycle into a per-slot story: five pipeline steps, each with a
// state, a short chip summary, the evidence sentences ("why") and the request to
// open. The verdict (`status`) is the lifecycle's own overallStatus, raised only
// by the session's redundancy findings that name this slot (slotRedundancies) -
// the same findings the lifecycle grouper raises for the ad unit, so a duplicate GAM
// call is never green here while the sign-off report flags it. This module decides how each STEP is presented, and for
// USB (adb logcat) rows it downgrades steps we could not have seen to `unknown`
// instead of showing them as missing.

import { adapterErrorName } from '../validation/mediationFillResult';

const STEP_DEFS = [
  { id: 'prebid', label: 'Prebid' },
  { id: 'amazon', label: 'APS' },
  { id: 'gam', label: 'GAM' },
  { id: 'winner', label: 'Winner' },
  { id: 'impression', label: 'Impression' },
  // Evidence only (WP1, D6/D7): OMID beacons seen for this slot. NEVER feeds the
  // verdict, `status` or the fail-slot counts - beacon visibility depends on
  // cert-pinned verification hosts, so absence/partial is weak evidence. The label
  // says "measured": a geometry beacon is not an MRC "viewable" claim.
  { id: 'viewable', label: 'Viewability measured' },
];

const VIEWABLE_OMID_ONLY = 'OMID beacons only; Google ActiveView not evaluated.';

// On USB rows these are artefacts of what logcat cannot show, not failures.
const USB_SUPPRESSED_CODES = new Set(['gam_no_fill', 'no_impression', 'usb_not_visible']);

const fmtPrice = (p) => {
  const n = Number(p);
  return Number.isFinite(n) ? n.toFixed(2) : String(p);
};

function issuesOf(lc) {
  return Array.isArray(lc && lc.issues) ? lc.issues.filter(Boolean) : [];
}

// ── Redundancy findings (detectRedundancies / the proxy's `redundancies`) ──
// Session-level findings; only these name a slot. A repeat-request finding covers
// its whole ad-unit group, so it is pinned to the slots whose own refreshFlag is
// the offence (the first request of a duplicate pair is not the duplicate).
const REPEAT_FLAG = { duplicate: 'duplicate', rapid_refresh: 'rapid', flood: 'flood' };
const FINDING_STATUS = { error: 'fail', warn: 'warn', review: 'review' };
const STATUS_RANK = { fail: 0, warn: 1, review: 2, pass: 3 };
export const REDUNDANCY_LABEL = {
  duplicate: 'Duplicate GAM request',
  rapid_refresh: 'Early reload',
  flood: 'Request flood',
  double_impression: 'Double impression',
};

/** The findings in `findings` that name this slot -> [{ type, label, severity, status, detail, rowKeys }]. */
export function slotRedundancies(lc, findings) {
  if (!lc || !Array.isArray(findings) || !findings.length) return [];
  const p = lc.phases || {};
  const gamKey = p.gam && p.gam.rowKey;
  const impKey = p.impression && p.impression.rowKey;
  const out = [];
  for (const f of findings) {
    if (!f || !Array.isArray(f.rowKeys)) continue;
    const flag = REPEAT_FLAG[f.type];
    const hit = flag
      ? lc.refreshFlag === flag && gamKey != null && f.rowKeys.includes(gamKey)
      : f.type === 'double_impression' && impKey != null && f.rowKeys.includes(impKey);
    if (!hit) continue;
    out.push({
      type: f.type,
      label: REDUNDANCY_LABEL[f.type] || f.type,
      severity: f.severity,
      status: FINDING_STATUS[f.severity] || 'review',
      detail: f.detail || '',
      rowKeys: f.rowKeys,
    });
  }
  return out;
}

const raiseStatus = (status, extra) => extra.reduce(
  (w, r) => ((STATUS_RANK[r.status] ?? 3) < (STATUS_RANK[w] ?? 3) ? r.status : w),
  status,
);

export function isUsbLifecycle(lc) {
  const row = lc && lc.phases && lc.phases.gam && lc.phases.gam.row;
  return !!(row && row.captureSource === 'logcat');
}

function worst(level, a) {
  const rank = { fail: 2, warn: 1 };
  return (rank[a] || 0) > (rank[level] || 0) ? a : level;
}

function mediationWinnerName(med) {
  if (!med || !med.winner) return null;
  const b = Array.isArray(med.buyers) ? med.buyers.find((x) => x && x.id === med.winner) : null;
  return (b && b.name) || String(med.winner);
}

function custParam(lc, ...names) {
  const cp = (lc && lc.custParams) || {};
  for (const n of names) if (cp[n] != null && cp[n] !== '') return cp[n];
  return null;
}

function truncationWhy(lc) {
  const gamRow = lc.phases && lc.phases.gam && lc.phases.gam.row;
  const all = [...issuesOf(lc), ...(gamRow && Array.isArray(gamRow.issues) ? gamRow.issues : [])];
  const t = all.find((i) => i && i.code === 'ingest_url_truncated');
  if (!t) return null;
  const m = /(\d+)\s*(?:chars|characters)/i.exec(t.msg || '');
  const n = m ? m[1] : null;
  return `URL cut${n ? ` at ${n} chars` : ''} by logcat: params after the cut (a3p, later cust_params) are unknown, not missing`;
}

export const LOW_CONFIDENCE_TIME_WHY = 'Low confidence (time-window match): this beacon was attributed to the slot by timing only, so a concurrent slot may own it';

// ── Viewability measured (evidence step) ─────────────────────────────────
// Reads lc.omidSessions[] (attached by lifecycle/omidJoin.js; absent on an older
// proxy -> treated as none). State vocabulary: ok = a geometry beacon was seen
// (NOT an MRC viewable claim); warn = a partial/odd session; absent = no beacon
// (neutral, the hosts may be pinned); unknown = USB capture cannot carry OMID.
function levelOfIssue(i) { return (i && (i.level || i.severity)) || ''; }

function omidSessionState(s) {
  const issues = Array.isArray(s && s.issues) ? s.issues : [];
  const codes = new Set(issues.map((i) => i && i.code));
  const pct = Number(s && s.percentInViewMax);
  const finished = Array.isArray(s && s.phases) && s.phases.includes('sessionFinish');
  if (pct > 0 && !issues.some((i) => levelOfIssue(i) === 'fail')) return 'ok';
  // Started but no impression: only a failure-ish signal once the session finished;
  // a capture that simply ended mid-ad also looks like this, so stay neutral.
  if (codes.has('omid_no_impression')) return finished ? 'warn' : 'absent';
  if (codes.has('omid_no_geometry') || codes.has('omid_zero_pct')
    || codes.has('omid_session_without_start') || codes.has('omid_out_of_order')) return 'warn';
  return 'absent';
}

// ── Google ActiveView lines (viewability-activeview WP2; copy: docs/sdlc/viewability-activeview/20-requirements.md
// "UI copy for unsupported paths"). The wording rule: nothing says "viewable" / "not viewable" as a
// finding unless the ping literally carried that `type`; nothing claims MRC viewability; nothing prints
// "(on screen N s)" until AC 2.60 (a real capture) confirms which bucket means what.
export const VIEWABLE_USB_WHY = 'ActiveView and OMID are not visible in USB capture. This is unknown, not a viewability failure.';
export const ACTIVEVIEW_FOOTER = "Google's verdict is as reported by the ActiveView ping; GAM Active View reporting is the proof of record.";
export const ACTIVEVIEW_ABSENT_FOOTER = 'No Google ActiveView ping was attributed to this slot; absence is not evidence of a failure.';
export const NOT_VIEWABLE_CODE = 'activeview_not_viewable';

const AV_JOIN_TEXT = {
  xai: 'ActiveView joined to this slot by xai (proven)',
  correlatorProven: 'ActiveView joined to this slot by correlator and ad unit (proven)',
  correlatorRefreshed: 'ActiveView joined to this slot by correlator and ad unit on a refreshed slot (inferred)',
  correlatorOnly: 'ActiveView joined to this slot by correlator only (inferred)',
  window: 'ActiveView joined to this slot by time window (inferred)',
};

function avJoinKey(e) {
  if (e.joinedBy === 'xai') return 'xai';
  if (e.joinedBy === 'correlator') {
    if (e.tier === 'proven') return 'correlatorProven';
    return e.iu ? 'correlatorRefreshed' : 'correlatorOnly';
  }
  return 'window';
}

// Why an explicit noviewable that is not the deciding ping is not counted.
function avNotCountedWhy(e, supersededBy) {
  if (e.tier === 'proven') return supersededBy ? 'a later ping for this creative supersedes it; not counted' : 'another ping decides this slot; not counted';
  if (e.joinedBy === 'iu_window') return 'joined by time window, inferred; not counted';
  if (e.joinedBy === 'correlator') return e.iu ? 'joined by correlator and ad unit on a refreshed slot, inferred; not counted' : 'joined by correlator only, inferred; not counted';
  return 'not counted';
}

function hasNonZeroBucket(e) {
  const nz = (a) => Array.isArray(a) && a.some((n) => Number(n) > 0);
  return nz(e.tosBuckets) || nz(e.mtosBuckets);
}

const hasBuckets = (e) => (Array.isArray(e.tosBuckets) && e.tosBuckets.length > 0) || (Array.isArray(e.mtosBuckets) && e.mtosBuckets.length > 0);

/**
 * Classify one activeView[] entry for the step. kind:
 *   'decider'    explicit noviewable, proven, latest (the slot carries the issue)
 *   'viewable'   explicit type=viewable (counted `ok` only when proven)
 *   'noviewable' explicit noviewable that is NOT the decider (inferred / superseded): evidence only
 *   'measured'   xai-matched app-style ping (no type) with a non-zero bucket
 *   'zero'       xai-matched app-style ping, buckets present but all zero
 *   'nobuckets'  xai-matched app-style ping without bucket data
 *   'other'      anything else (startEarly, other type, untyped non-xai join)
 */
function classifyEntry(e, deciderKey, hasIssue) {
  const type = typeof e.type === 'string' ? e.type.toLowerCase() : '';
  if (type === 'noviewable') return hasIssue && e.key === deciderKey ? 'decider' : 'noviewable';
  if (type === 'viewable') return 'viewable';
  if (!type && e.joinedBy === 'xai') return hasNonZeroBucket(e) ? 'measured' : (hasBuckets(e) ? 'zero' : 'nobuckets');
  return 'other';
}

function avLine(e, kind, supersededBy) {
  const t = e.type || 'none';
  switch (kind) {
    case 'decider': return `Google ActiveView reports not viewable (ping type=${t}).`;
    case 'viewable': return e.tier === 'proven'
      ? `Google ActiveView reports viewable (ping type=${t}).`
      : `Google ActiveView reports viewable (ping type=${t}; ${avNotCountedWhy(e, false).replace('; not counted', '')}; not counted).`;
    case 'noviewable': return `Google ActiveView reports not viewable (ping type=${t}; ${avNotCountedWhy(e, supersededBy)}).`;
    case 'measured': return 'Google ActiveView measurement ping fired for this creative (matched by xai); the viewable verdict is in GAM Active View reporting.';
    case 'zero': return 'Google ActiveView measurement ping fired for this creative (matched by xai); exposure buckets are all zero; the viewable verdict is in GAM Active View reporting.';
    case 'nobuckets': return 'Google ActiveView measurement ping fired for this creative (matched by xai); it reported no exposure buckets; the viewable verdict is in GAM Active View reporting.';
    default: return `Google ActiveView ping attributed to this slot (type ${t}); it carries no viewable verdict.`;
  }
}

/** 'adx' | 'direct' when the GAM response body shows Google's own demand rendered; else null. */
function googleCreativeSource(lc) {
  const row = lc && lc.phases && lc.phases.gam && lc.phases.gam.row;
  const cs = row && row.validData && row.validData.expanded && row.validData.expanded.creativeSource;
  const src = cs && cs.source;
  return src === 'adx' || src === 'direct' ? src : null;
}

function slotHasXaiKey(lc) {
  const row = lc && lc.phases && lc.phases.gam && lc.phases.gam.row;
  const ex = (row && row.validData && row.validData.expanded) || {};
  return (Array.isArray(ex.impXaiAll) && ex.impXaiAll.length > 0) || (typeof ex.impXai === 'string' && ex.impXai !== '');
}

function buildViewableStep(lc, usb, opts) {
  const sessions = (Array.isArray(lc && lc.omidSessions) ? lc.omidSessions : []).filter(Boolean);
  const geometry = sessions.filter((s) => Number(s.percentInViewMax) > 0);
  const omidKeys = [];
  for (const s of sessions) for (const k of (Array.isArray(s.rowKeys) ? s.rowKeys : [])) if (k != null && !omidKeys.includes(k)) omidKeys.push(k);
  const omidLead = (geometry[0] && Array.isArray(geometry[0].rowKeys) && geometry[0].rowKeys[0]) ?? omidKeys[0] ?? null;

  // Field absent (older proxy) = ActiveView NOT evaluated: today's step, byte for byte (DA7).
  const evaluated = !!lc && Array.isArray(lc.activeView);
  // ActiveView is ignored entirely on a USB lifecycle (DA11).
  const entries = evaluated && !usb ? lc.activeView.filter((e) => e && typeof e === 'object') : [];

  const avKeys = [];
  for (const e of entries) for (const k of (Array.isArray(e.rowKeys) ? e.rowKeys : [e.key])) if (k != null && !avKeys.includes(k)) avKeys.push(k);
  const rowKeys = [...avKeys];
  for (const k of omidKeys) if (!rowKeys.includes(k)) rowKeys.push(k);

  const issue = !usb && evaluated ? issuesOf(lc).find((i) => i.code === NOT_VIEWABLE_CODE) : null;
  const hasIssue = !!issue;
  const decider = hasIssue ? (entries.find((e) => e.key === issue.rowKey) || entries.find((e) => e.status === 'warn')) : null;
  const deciderKey = decider ? decider.key : null;
  const kinds = entries.map((e) => classifyEntry(e, deciderKey, hasIssue));
  // Latest entry per group (xai, or per slot when absent): earlier ones are "superseded".
  const supersededBy = entries.map((e, i) => entries.some((o, j) => j > i && (o.xai || '') === (e.xai || '')));

  // Lead entry for rowKey: the decider, else the latest proven viewable / measured ping.
  let leadIdx = -1;
  if (hasIssue) leadIdx = entries.findIndex((e) => e.key === deciderKey);
  if (leadIdx < 0) {
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      if ((kinds[i] === 'viewable' && entries[i].tier === 'proven') || kinds[i] === 'measured') { leadIdx = i; break; }
    }
  }
  const lead = leadIdx >= 0 ? entries[leadIdx].key : (omidLead ?? rowKeys[0] ?? null);
  const base = {
    evidenceOnly: !hasIssue,
    rowKeys: evaluated ? rowKeys : omidKeys,
    rowKey: evaluated ? lead : omidLead,
    ...(evaluated ? { avEvaluated: true } : {}),
  };

  // USB capture carries no OMID beacons: unknown, never absent/fail (unless a
  // geometry beacon somehow is attached - kept as a guard).
  if (usb && !geometry.length) {
    return { ...base, state: 'unknown', summary: '', why: [evaluated ? VIEWABLE_USB_WHY : 'No OMID beacons were logged by the device. This is unknown, not a viewability failure.'] };
  }

  const states = sessions.map(omidSessionState);
  const omidState = states.includes('warn') ? 'warn' : states.includes('ok') ? 'ok' : 'absent';
  const why = [];
  let summary = '';

  // ActiveView first (Q3), OMID second.
  let avOk = false;
  let avSummary = '';
  if (evaluated && !usb) {
    if (hasIssue && !decider) why.push(`${issue.msg || 'Google ActiveView reports not viewable'}.`);
    if (leadIdx >= 0) why.push(avLine(entries[leadIdx], kinds[leadIdx], supersededBy[leadIdx]));
    entries.forEach((e, i) => { if (i !== leadIdx) why.push(avLine(e, kinds[i], supersededBy[i])); });
    entries.forEach((e) => why.push(AV_JOIN_TEXT[avJoinKey(e)]));
    const okViewable = entries.some((e, i) => kinds[i] === 'viewable' && e.tier === 'proven');
    const okMeasured = entries.some((e, i) => kinds[i] === 'measured');
    avOk = okViewable || okMeasured;
    avSummary = hasIssue ? 'Google not viewable' : okViewable ? 'Google viewable' : okMeasured ? 'ActiveView measured' : '';
  }
  const state = hasIssue ? 'warn' : avOk ? 'ok' : omidState;

  if (!sessions.length) {
    why.push('No OMID beacon seen. Verification hosts may be pinned or unlogged; this does not mean the ad was unmeasured.');
  } else {
    sessions.forEach((s, idx) => {
      const st = states[idx];
      if (st === 'ok') {
        why.push(`Geometry seen, max ${s.percentInViewMax}% in view (vendor ${s.vendor || 'unknown'}). This is not an MRC viewable claim.`);
      } else if (st === 'warn') {
        const msgs = (s.issues || []).filter((i) => i && (i.message || i.msg)).map((i) => i.message || i.msg);
        why.push(`${s.vendor ? `${s.vendor}: ` : ''}${msgs.join('; ') || 'Partial OMID session'}`);
      } else {
        why.push('OMID session started but no impression or session finish was seen - the capture may have ended mid-ad.');
      }
      why.push(`Session joined to this slot by ${s.joinedBy === 'correlator' ? 'correlator' : 'time window (inferred)'}`);
    });
    if (omidState === 'ok') {
      const max = Math.max(...geometry.map((s) => Number(s.percentInViewMax)));
      summary = `max ${max}%`;
    }
  }
  if (!evaluated) {
    why.push(VIEWABLE_OMID_ONLY);
    return { ...base, state, summary, why };
  }
  // ActiveView evaluated: say how many pings could not be attributed (E7) before the footer.
  const unattached = Number(opts && opts.activeViewRollup && opts.activeViewRollup.unattached) || 0;
  if (unattached > 0 && !slotHasXaiKey(lc)) {
    why.push(`${unattached} ActiveView ping${unattached === 1 ? '' : 's'} in this session could not be attributed to a slot: the GAM response body was not captured, so the xai key is missing.`);
  }
  why.push(entries.length ? ACTIVEVIEW_FOOTER : ACTIVEVIEW_ABSENT_FOOTER);
  return { ...base, state, summary: avSummary || summary, why };
}

/**
 * @param {object} lc  one slot lifecycle
 * @param {object} [opts]
 * @param {Array}  [opts.duplicateBills]  from utils/mergeBillingPings.js
 *   billDuplicatesForLifecycle(): groups of identical billing pings (count > 1).
 *   The Winner step's why[] states N; verdict, status and counts are unchanged
 *   (a duplicate bill is evidence for the engineer, not a verdict input).
 * @param {Array}  [opts.redundancies]  the session's redundancy findings; those naming this slot
 *   (slotRedundancies) become `story.redundancy`, a Slot why-line each, and can raise `status`.
 * @param {object} [opts.activeViewRollup]  the session's `{ pingCount, attached, unattached, ... }`
 *   (validation/activeView.js activeViewRollup, or the proxy summary's `activeView`); only
 *   `unattached` is read, to explain pings that could not be tied to a slot.
 */
export function buildSlotStory(lc, opts) {
  const dupBills = (opts && Array.isArray(opts.duplicateBills) ? opts.duplicateBills : []).filter((d) => d && d.count > 1);
  const phases = (lc && lc.phases) || {};
  const prebid = phases.prebid || {};
  const amazon = phases.amazon || {};
  const gam = phases.gam || {};
  const imp = phases.impression || {};
  const med = phases.mediation || {};
  const usb = isUsbLifecycle(lc);
  const redundancy = slotRedundancies(lc, opts && opts.redundancies);

  // Issues grouped by phase; those without a phase (older proxy) fall to slot level.
  const byPhase = {};
  const slotWhy = [];
  for (const i of issuesOf(lc)) {
    if (usb && USB_SUPPRESSED_CODES.has(i.code)) continue;
    if (i.code === 'ingest_url_truncated' || i.code === 'ingest_request_only') continue; // surfaced via USB wording
    const ph = i.phase && STEP_PHASES.has(i.phase) ? (i.phase === 'click' ? 'impression' : i.phase) : null;
    if (!ph) {
      slotWhy.push(i.msg || String(i.code || 'issue'));
      continue;
    }
    (byPhase[ph] = byPhase[ph] || []).push(i);
  }
  for (const r of redundancy) slotWhy.push(`${r.label}${r.severity === 'review' ? ' (needs review)' : ''}: ${r.detail}`);

  const phaseState = (ph, fired, unknownWhenUnfired) => {
    let level = null;
    for (const i of byPhase[ph] || []) level = worst(level, i.level);
    if (level === 'fail') return 'fail';
    if (level === 'warn') return 'warn';
    if (fired) return 'ok';
    return unknownWhenUnfired ? 'unknown' : 'absent';
  };
  const issueWhy = (ph) => (byPhase[ph] || []).filter((i) => i.level === 'fail' || i.level === 'warn' || i.level === 'info').map((i) => i.msg);

  const hbPb = gam.hbPb || custParam(lc, 'hb_pb');
  const amznbid = amazon.amznB || custParam(lc, 'amznbid', 'amzn_b', 'amznb');

  // ── Prebid
  const prebidStep = (() => {
    const why = [];
    let summary = '';
    let fired = !!prebid.fired;
    const bids = Array.isArray(prebid.bids) ? prebid.bids : [];
    const pChaos = prebid.chaos || (prebid.row && (prebid.row.chaos || (prebid.row.validData && prebid.row.validData.chaos))) || null;
    if (fired && pChaos && (pChaos.action === 'block' || pChaos.action === 'mock')) {
      // The proxy answered this auction itself (Chaos) — its empty/synthetic reply is not "0 bids".
      const verb = pChaos.action === 'mock' ? 'mocked' : 'blocked';
      why.push(`Prebid request ${verb} by Chaos rule${pChaos.id ? ` ${pChaos.id}` : ''} — no bids by design`);
      why.push(...issueWhy('prebid'));
      return { state: 'absent', summary: `${verb} (Chaos)`, why, rowKey: prebid.rowKey ?? null };
    }
    if (fired) {
      const priced = bids.filter((b) => Number(b.price) > 0);
      summary = `${priced.length || bids.length} bid${(priced.length || bids.length) === 1 ? '' : 's'}`;
      if (prebid.winner) summary += ` · ${fmtPrice(prebid.winner.price)}`;
      why.push(`Prebid auction captured: ${bids.length} bidder response${bids.length === 1 ? '' : 's'}${prebid.winner ? `, winner ${prebid.winner.seat} at ${fmtPrice(prebid.winner.price)}` : ', no winner'}${prebid.timeouts ? `, ${prebid.timeouts} timeout${prebid.timeouts === 1 ? '' : 's'}` : ''}`);
    } else if (usb) {
      if (hbPb) {
        fired = true;
        summary = `hb_pb ${hbPb}`;
        why.push(`hb_pb=${hbPb} in GAM cust_params - Prebid ran`);
      } else {
        why.push('Prebid traffic is not visible in USB capture');
      }
    } else {
      why.push('No Prebid auction request matched this GAM request');
    }
    why.push(...issueWhy('prebid'));
    return { state: phaseState('prebid', fired, usb), summary, why, rowKey: prebid.rowKey ?? null };
  })();

  // ── APS
  const amazonStep = (() => {
    const why = [];
    let summary = '';
    let fired = !!amazon.fired;
    const aChaos = amazon.chaos || (amazon.row && (amazon.row.chaos || (amazon.row.validData && amazon.row.validData.chaos))) || null;
    if (fired && aChaos && (aChaos.action === 'block' || aChaos.action === 'mock')) {
      const verb = aChaos.action === 'mock' ? 'mocked' : 'blocked';
      why.push(`APS request ${verb} by Chaos rule${aChaos.id ? ` ${aChaos.id}` : ''} — no bid by design`);
      return { state: 'absent', summary: `${verb} (Chaos)`, why, rowKey: amazon.rowKey ?? null };
    }
    if (fired) {
      summary = amazon.bidStatus || (amazon.passedToGAM ? 'passed to GAM' : 'request seen');
      why.push(`APS ${amazon.cached ? 'bid reused from SDK cache' : 'request captured'}${amazon.bidStatus ? ` (${amazon.bidStatus})` : ''}${amazon.passedToGAM ? '; amzn bid reached GAM' : ''}`);
    } else if (usb) {
      if (amznbid) {
        fired = true;
        summary = 'bid in GAM';
        why.push(`amznbid=${amznbid} in GAM cust_params - APS ran`);
      } else {
        why.push('APS traffic is not visible in USB capture');
      }
    } else {
      why.push('No APS request matched this GAM request');
    }
    why.push(...issueWhy('amazon'));
    return { state: phaseState('amazon', fired, usb), summary, why, rowKey: amazon.rowKey ?? null };
  })();

  // ── GAM
  const gamStep = (() => {
    const why = [];
    const sz = Array.isArray(lc && lc.sizes) ? lc.sizes.join(',') : (lc && lc.sizes) || '';
    const unitTail = String(lc && lc.adUnit || gam.adUnit || '').split('/').filter(Boolean).slice(-2).join('/');
    const summary = [unitTail, sz].filter(Boolean).join(' ');
    why.push(gam.fired ? `GAM ad request captured${gam.responseType ? ` (response: ${gam.responseType})` : ''}` : 'No GAM request');
    if (hbPb || gam.hbBidder) why.push(`Targeting sent to GAM: ${[gam.hbBidder && `hb_bidder=${gam.hbBidder}`, hbPb && `hb_pb=${hbPb}`].filter(Boolean).join(', ')}`);
    if (usb) {
      const t = truncationWhy(lc);
      if (t) why.push(t);
    }
    why.push(...issueWhy('gam'));
    return { state: phaseState('gam', !!gam.fired, false), summary, why, rowKey: gam.rowKey ?? null };
  })();

  // ── Winner
  const medName = mediationWinnerName(med);
  const winnerInfo = (() => {
    if (medName) return { name: medName, via: med.winnerSource === 'bill' || med.winnerSource === 'impression' ? med.winnerSource : 'adapter', kind: 'mediation' };
    if (gam.adapterLabel) return { name: gam.adapterLabel, via: 'adapter', kind: 'adapter' };
    if (prebid.winner && gam.hbBidder && prebid.winner.seat === gam.hbBidder) return { name: prebid.winner.seat, via: 'prebid', kind: 'prebid' };
    // Google's own demand renders with no adapter and no hb_bidder: the proxy's
    // creativeSource (read from the GAM response body) is the only signal.
    const src = googleCreativeSource(lc);
    if (src === 'adx') return { name: 'Google AdX', via: 'creative', kind: 'google' };
    if (src === 'direct') return { name: 'Google (GAM line item)', via: 'creative', kind: 'google' };
    return null;
  })();
  const winnerStep = (() => {
    const why = [];
    let state;
    let summary = '';
    const noFill = issuesOf(lc).some((i) => i.code === 'gam_no_fill');
    if (winnerInfo) {
      summary = winnerInfo.via === 'bill' ? `${winnerInfo.name} (bill)` : winnerInfo.via === 'impression' ? `${winnerInfo.name} (impression beacon)` : winnerInfo.name;
      // Net winning CPM from the GAM response (boosted_cpm_bid_usd_minus_revenue_sharing).
      if (gam.winnerCpm != null) summary += ` · $${fmtPrice(gam.winnerCpm)}`;
      if (winnerInfo.kind === 'mediation') {
        why.push(winnerInfo.via === 'bill'
          ? `${winnerInfo.name} won via SDK bidding - named by its billing notice (GAM response not decoded)`
          : winnerInfo.via === 'impression'
            ? `${winnerInfo.name} won via SDK bidding - named by its own impression beacon (GAM response not decoded)`
            : `${winnerInfo.name} won via SDK bidding - named by the GAM adapter label`);
      } else if (winnerInfo.kind === 'google') {
        why.push(winnerInfo.name === 'Google AdX'
          ? `GAM response creative is served by AdX (Google demand)${gam.winnerCpm != null ? ` at ${fmtPrice(gam.winnerCpm)}` : ''}`
          : 'GAM served its own creative: no Prebid, APS or SDK-bidding signal in the response (direct, house or AdX line item)');
      } else if (winnerInfo.kind === 'adapter') {
        why.push(`GAM response names ${winnerInfo.name} as the winning adapter${gam.winnerCpm != null ? ` at ${fmtPrice(gam.winnerCpm)}` : ''}`);
      } else {
        why.push(`Prebid winner ${winnerInfo.name} matches hb_bidder sent to GAM`);
      }
      if (prebid.winner && winnerInfo.kind !== 'prebid') {
        why.push(`Prebid's best bid (${prebid.winner.seat} ${fmtPrice(prebid.winner.price)}) did not win`);
      }
      state = 'ok';
      const fr = med.fillResult;
      if (fr && fr.failed) {
        summary += fr.timedOut ? ' · load timed out' : ' · load failed';
        why.push(fr.timedOut
          ? `The Google SDK reported that ${winnerInfo.name}'s ad timed out before loading${fr.ttrMs != null ? ` (${fr.ttrMs} ms)` : ''}: nothing rendered`
          : `The Google SDK reported that ${winnerInfo.name}'s SDK failed to load the ad${fr.errorCode ? ` (${errorText(fr.errorCode, winnerInfo.name)})` : ''}${fr.ttrMs != null ? ` after ${fr.ttrMs} ms` : ''}: nothing rendered`);
      } else if (fr) {
        why.push(`The Google SDK reported the ad loaded${fr.ttrMs != null ? ` in ${fr.ttrMs} ms` : ''}`);
      } else if (unconfirmedRender(winnerInfo, imp, usb)) {
        why.push(`No load report from the Google SDK and no impression: ${winnerInfo.name}'s ad is not proven to have rendered`);
      }
    } else if (usb) {
      state = 'unknown';
      why.push('GAM response is not visible in USB capture');
    } else if (noFill) {
      state = 'absent';
      why.push('GAM returned no ad (no fill)');
    } else {
      state = 'absent';
      why.push('No winner identified from the GAM response');
    }
    for (const d of dupBills) {
      why.push(`Billing notice from ${d.name || d.buyerId || 'the buyer'} fired ${d.count} times in this slot (identical requests; possible double billing - confirm in the buyer's reporting)`);
    }
    why.push(...issueWhy('winner'));
    const issued = phaseState('winner', false, false);
    if (issued === 'fail' || issued === 'warn') state = issued;
    return { state, summary, why, rowKey: gam.rowKey ?? null };
  })();

  // ── Impression
  const impressionStep = (() => {
    const why = [];
    if (imp.fired) why.push(`Impression beacon fired${imp.latencyMs != null ? ` ${imp.latencyMs} ms after the GAM request` : ''}`);
    else if (usb) why.push('No impression beacon seen - beacons may not be logged in USB capture');
    else why.push('No impression beacon matched this GAM request');
    // Tier-3 time-only joins can be stolen by a concurrent slot; surface it, never change the join.
    const lowConfidence = !!(imp.fired && imp.matchTier === 'time');
    if (lowConfidence) why.push(LOW_CONFIDENCE_TIME_WHY);
    why.push(...issueWhy('impression'));
    if (phases.click && phases.click.fired) why.push('Click recorded');
    return {
      state: phaseState('impression', !!imp.fired, usb),
      summary: imp.fired ? (imp.latencyMs != null ? `${imp.latencyMs} ms` : 'fired') : '',
      why,
      rowKey: imp.rowKey ?? null,
      ...(lowConfidence ? { lowConfidence: true } : {}),
    };
  })();

  const built = {
    prebid: prebidStep, amazon: amazonStep, gam: gamStep, winner: winnerStep, impression: impressionStep,
    viewable: buildViewableStep(lc, usb, opts),
  };
  const steps = STEP_DEFS.map((d) => ({ id: d.id, label: d.label, ...built[d.id] }));

  // ── Verdict
  const parts = [];
  if (winnerInfo) {
    parts.push(winnerInfo.kind === 'mediation' ? `${winnerInfo.name} won via SDK bidding`
      : winnerInfo.kind === 'prebid' ? `${winnerInfo.name} won via Prebid` : `${winnerInfo.name} won`);
    if (prebid.winner && winnerInfo.kind !== 'prebid') parts.push(`Prebid ${fmtPrice(prebid.winner.price)} lost`);
  }
  // Fill is decided once, before the verdict, so the summary line can't
  // contradict the Filled / No fill badge.
  const fill = buildFill({ lc, usb, prebid, amazon, gam, med, imp, hbPb, amznbid, winnerInfo, steps });
  // evidenceOnly steps (Viewability measured) never become the verdict's "problem".
  const counted = steps.filter((s) => !s.evidenceOnly);
  const problem = counted.find((s) => s.state === 'fail') || counted.find((s) => s.state === 'warn');
  let tail;
  if (problem) {
    const issue = (byPhase[problem.id] || []).find((i) => i.level === problem.state);
    tail = (issue && issue.msg) || problem.why[problem.why.length - 1];
  } else if (slotWhy.length && lc && lc.overallStatus !== 'pass') {
    tail = slotWhy[0];
  } else if (imp.fired) {
    tail = 'rendered';
  } else if (usb) {
    tail = 'outcome not visible (USB capture)';
  } else if (fill && fill.state === 'filled') {
    // GAM served a creative; we just didn't see its impression ping (often QUIC).
    tail = 'filled - no impression beacon seen';
  } else if (fill && fill.state === 'unknown' && fill.label === 'Unconfirmed') {
    tail = 'render unconfirmed - no load report or impression seen';
  } else if (fill && fill.state === 'unknown') {
    // No GAM response to read (body not kept) and no impression: "not filled" would be a guess.
    tail = 'fill unknown (GAM response not kept)';
  } else {
    tail = 'not filled';
  }
  if (tail === 'rendered' && imp.fired) tail = 'rendered - impression fired';
  parts.push(tail);


  return {
    key: lc && lc.key,
    adUnit: lc && lc.adUnit,
    slotLabel: lc && lc.slotLabel,
    sizes: lc && lc.sizes,
    isRefresh: !!(lc && lc.isRefresh),
    usb,
    steps,
    slotWhy,
    status: raiseStatus((lc && lc.overallStatus) || 'pass', redundancy),
    redundancy,
    verdict: parts.join(' · '),
    fill,
    winner: winnerInfo ? { name: winnerInfo.name, kind: winnerInfo.kind, via: winnerInfo.via } : null,
    // Info signal, not a verdict input: identical billing pings seen in this slot.
    duplicateBilling: dupBills.map((d) => ({ buyerId: d.buyerId, name: d.name, count: d.count, rowKey: d.rowKey, code: 'duplicate_billing_ping', level: 'info' })),
  };
}

// Chaos block/mock marker on a Prebid/APS phase (proxy-stamped row.chaos), or null.
function phaseChaos(ph) {
  const c = ph && (ph.chaos || (ph.row && (ph.row.chaos || (ph.row.validData && ph.row.validData.chaos))));
  return c && (c.action === 'block' || c.action === 'mock') ? c : null;
}

// An SDK-bidding winner named only by GAM's adapter label, with no impression: the buyer's
// SDK still has to fetch its ad, so without the Google SDK's fill-result report the render is
// unproven (a bill or impression `via` is render evidence and stays Filled). MITM only.
function unconfirmedRender(winnerInfo, imp, usb) {
  return !usb && !imp.fired && !!winnerInfo && winnerInfo.kind === 'mediation' && winnerInfo.via === 'adapter';
}

// "error 1001 "No Fill"" for Meta, bare "error 1001" for other buyers.
function errorText(code, who) {
  const name = adapterErrorName(code, who);
  return `error ${code}${name ? ` "${name}"` : ''}`;
}

function loadFailedNext(code, who) {
  const meta = /meta|facebook/i.test(String(who || ''));
  if (meta && String(code) === '1000') return 'Next: the buyer\'s SDK could not reach its server - check the proxy, the SSL exclude list and Chaos rules for its hosts';
  if (meta && String(code) === '1002') return 'Next: Meta refuses reloads of this placement after recent failures - wait 30 minutes before retrying, then fix the first failure';
  return 'Next: for a test auction (adtest=on), put the buyer\'s SDK in test mode too, or the buyer returns no fill; otherwise check the buyer\'s placement and SDK setup';
}

// ── Fill outcome + no-fill debugging ──────────────────────────────────────
// filled: something rendered or a winner is known. nofill: the server saw an
// empty GAM response (gam_no_fill). unknown: no response to read (USB capture, or
// a MITM row whose body was not kept). For nofill/unknown, `why` walks the demand
// path in pipeline order so the cause is readable top to bottom.
function buildFill({ lc, usb, prebid, amazon, gam, med, imp, hbPb, amznbid, winnerInfo, steps }) {
  const noFill = issuesOf(lc).some((i) => i.code === 'gam_no_fill');
  const winStep = steps.find((x) => x.id === 'winner');
  const fr = med && med.fillResult;
  if (!noFill && fr && fr.failed && !imp.fired) {
    const who = winnerInfo ? winnerInfo.name : 'The winning buyer';
    return {
      state: 'nofill',
      label: fr.timedOut ? 'Load timed out' : 'Load failed',
      why: fr.timedOut
        ? [
          `${who} won GAM's auction, but its ad timed out before loading${fr.ttrMs != null ? ` (${fr.ttrMs} ms)` : ''}`,
          'Next: check whether the slot was on screen and the creative\'s assets were reachable through the proxy (Chaos rules, SSL exclude list)',
        ]
        : [
          `${who} won GAM's auction, but its SDK failed to load the ad${fr.errorCode ? ` (${errorText(fr.errorCode, who)})` : ''}`,
          loadFailedNext(fr.errorCode, who),
        ],
    };
  }
  if (!noFill && !fr && unconfirmedRender(winnerInfo, imp, usb)) {
    return {
      state: 'unknown',
      label: 'Unconfirmed',
      why: [
        `${winnerInfo.name} won GAM's auction, but the Google SDK sent no load report and no impression fired`,
        'The buyer\'s SDK must still fetch its ad, so a named winner alone does not prove a render. The report can be late or skipped when the slot leaves the screen',
      ],
    };
  }
  if (!noFill && (imp.fired || (winStep && winStep.state === 'ok') || winnerInfo)) {
    const by = winnerInfo ? `Filled by ${winnerInfo.name}` : 'Filled';
    return { state: 'filled', label: 'Filled', why: [`${by}${imp.fired ? ' · impression fired' : ''}`] };
  }
  if (!noFill && !usb && gam.responseType) {
    return { state: 'filled', label: 'Filled', why: [`GAM returned a creative (response: ${gam.responseType}); no impression beacon matched`] };
  }

  const why = [];
  // Prebid
  const bids = Array.isArray(prebid.bids) ? prebid.bids : [];
  const priced = bids.filter((b) => Number(b.price) > 0);
  const pChaos = phaseChaos(prebid);
  if (prebid.fired && pChaos) {
    why.push(`Prebid: ${pChaos.action === 'mock' ? 'mocked' : 'blocked'} by Chaos rule${pChaos.id ? ` ${pChaos.id}` : ''}`);
  } else if (prebid.fired) {
    why.push(priced.length
      ? `Prebid: ${priced.length} bid${priced.length === 1 ? '' : 's'}${prebid.winner ? `, best ${prebid.winner.seat} ${fmtPrice(prebid.winner.price)}` : ''}`
      : `Prebid: no bids${prebid.nobids ? ` (${prebid.nobids} no-bid${prebid.nobids === 1 ? '' : 's'})` : ''}${prebid.timeouts ? `, ${prebid.timeouts} timeout${prebid.timeouts === 1 ? '' : 's'}` : ''}`);
  } else if (!usb) {
    why.push('Prebid: no auction matched to this slot');
  }
  if (hbPb) why.push(`hb_pb=${hbPb} sent to GAM${gam.hbBidder ? ` (hb_bidder=${gam.hbBidder})` : ''}`);
  else if (priced.length) why.push('Prebid had bids but no hb_pb reached GAM — targeting not set on the request');
  // APS
  const aChaos = phaseChaos(amazon);
  if (amazon.fired && aChaos) {
    why.push(`APS: ${aChaos.action === 'mock' ? 'mocked' : 'blocked'} by Chaos rule${aChaos.id ? ` ${aChaos.id}` : ''}`);
  } else if (amazon.fired) {
    why.push(`APS: ${amazon.bidStatus || (amznbid ? 'bid' : 'unknown')}${amznbid ? ` · amznbid=${amznbid} sent to GAM` : amazon.bidStatus === 'bid' ? ' but no amznbid on the GAM request' : ''}`);
  } else if (amznbid) {
    why.push(`APS: amznbid=${amznbid} sent to GAM`);
  } else if (!usb) {
    why.push('APS: no request or targeting seen');
  }
  // SDK bidding buyers
  const buyers = (med && Array.isArray(med.buyers)) ? med.buyers : [];
  if (buyers.length) {
    why.push(`SDK bidders: ${buyers.map((b) => `${b.name || b.id} ${String(b.outcome || 'unknown').replace(/_/g, ' ')}`).join(', ')}`);
  }
  // GAM
  const floor = gam.floorCpm != null ? Number(gam.floorCpm) : null;
  if (floor != null && Number.isFinite(floor)) {
    const best = prebid.winner ? Number(prebid.winner.price) : null;
    why.push(best != null && best < floor
      ? `GAM floor ${fmtPrice(floor)} is above the best Prebid bid ${fmtPrice(best)}`
      : `GAM floor ${fmtPrice(floor)}`);
  }
  if (lc && lc.isRefresh) why.push('Refresh request (demand is often thinner on refresh)');

  if (noFill) {
    const anyDemand = !!(hbPb || amznbid || priced.length || buyers.some((b) => b.outcome === 'filled' || b.outcome === 'won'));
    why.unshift('GAM returned an empty response — no creative served');
    why.push(anyDemand
      ? 'Next: demand reached GAM but nothing served — check line-item targeting for these key-values/sizes, the floor, and creative size eligibility'
      : 'Next: no demand reached GAM — check that this ad unit/size is configured in Prebid, APS and the mediation group, and that a backfill line item targets it');
    return { state: 'nofill', label: 'No fill', why };
  }
  why.unshift(usb
    ? 'Fill not visible: the GAM response is not in USB capture, and no billing notice or impression was logged for this slot'
    : 'Fill unknown: the GAM response was not parsed (body not kept) and no impression matched');
  return { state: 'unknown', label: 'Fill ?', why };
}

// 'viewable' carries the one counted ActiveView finding (activeview_not_viewable); it renders in the
// Viewability step and becomes the verdict tail through the first-counted-problem path, not in slotWhy.
const STEP_PHASES = new Set(['prebid', 'amazon', 'gam', 'impression', 'click', 'winner', 'viewable']);

/** Outcome known? -> { known, filled } */
function outcomeOf(story) {
  const st = story.fill && story.fill.state;
  return { known: st === 'filled' || st === 'nofill', filled: st === 'filled' };
}

export function summarizeSession(lifecycles, opts) {
  const list = Array.isArray(lifecycles) ? lifecycles : [];
  let failing = 0;
  let warning = 0;
  let filled = 0;
  let fillKnown = 0;
  const bidders = new Set();
  for (const lc of list) {
    const story = buildSlotStory(lc, opts);
    if (story.status === 'fail') failing += 1;
    else if (story.status === 'warn') warning += 1;
    const o = outcomeOf(story);
    if (o.known) {
      fillKnown += 1;
      if (o.filled) filled += 1;
    }
    const p = (lc && lc.phases) || {};
    for (const b of (p.prebid && p.prebid.bids) || []) if (b && b.seat && Number(b.price) > 0) bidders.add(String(b.seat));
    const w = mediationWinnerName(p.mediation);
    if (w) bidders.add(w);
  }
  return {
    slots: list.length,
    failing,
    warning,
    filled,
    fillKnown,
    fillRate: fillKnown ? filled / fillKnown : null,
    bidders: Array.from(bidders).sort(),
  };
}

// ── Timing waterfall (spec Step 2) ─────────────────────────────────────────
// Each phase's request on one axis, in ms relative to the GAM request leaving.
// Full rows come from `rowsByKey` (the dashboard's own rows carry durationMs);
// the lifecycle's projected phase rows are the fallback for the start time.
// Findings here are presentation notes: they never change the slot's status.

const toMs = (v) => {
  if (!v && v !== 0) return 0;
  if (typeof v === 'number') return v;
  const n = Date.parse(v);
  return Number.isNaN(n) ? 0 : n;
};
const startOf = (r) => (r ? toMs(r.requestStartedAt) || toMs(r.startedAt) || toMs(r.startedAtMs) || toMs(r.timing && r.timing.start) || toMs(r.startedAtISO) || toMs(r.timestamp) : 0);
const durOf = (r) => {
  const d = r && Number(r.durationMs);
  return Number.isFinite(d) && d >= 0 && r.durationMs != null ? d : null;
};
const fmtMs = (ms) => `${ms > 0 ? '+' : ''}${Math.round(ms)} ms`;

/**
 * @param {object} lc  one slot lifecycle
 * @param {Map|object} [rowsByKey]  `_k` -> full captured row
 * @returns {{ anchorKnown: boolean, entries: Array<{id,label,start,end,rowKey}>, notes: Array<{level,msg}> }}
 *   start/end are ms relative to the GAM request (end null when no response was observed).
 */
export function buildSlotTimeline(lc, rowsByKey) {
  const phases = (lc && lc.phases) || {};
  const lookup = (k) => {
    if (k == null || !rowsByKey) return null;
    return (typeof rowsByKey.get === 'function' ? rowsByKey.get(k) : rowsByKey[k]) || null;
  };
  const rowFor = (ph) => (ph ? lookup(ph.rowKey) || ph.row || null : null);

  const gamRow = rowFor(phases.gam);
  const anchor = startOf(gamRow) || toMs(lc && lc.timestamp);
  if (!anchor) return { anchorKnown: false, entries: [], notes: [] };

  const entries = [];
  const add = (id, label, row, rowKey) => {
    const s = startOf(row);
    if (!s) return null;
    const d = durOf(row);
    const e = { id, label, start: s - anchor, end: d == null ? null : s - anchor + d, rowKey: rowKey ?? null };
    entries.push(e);
    return e;
  };

  const pb = phases.prebid && phases.prebid.fired ? add('prebid', 'Prebid', rowFor(phases.prebid), phases.prebid.rowKey) : null;
  const aps = phases.amazon && phases.amazon.rowKey != null ? add('amazon', 'APS', rowFor(phases.amazon), phases.amazon.rowKey) : null;
  add('gam', 'GAM', gamRow, phases.gam && phases.gam.rowKey);

  // Billing notice: the winning SDK-bidding buyer's render ping. Only full rows
  // carry validData.kind, so this needs rowsByKey.
  const med = phases.mediation;
  if (med && Array.isArray(med.buyers)) {
    for (const b of med.buyers) {
      const bill = (b && Array.isArray(b.rowKeys) ? b.rowKeys : [])
        .map((k) => ({ k, r: lookup(k) }))
        .find(({ r }) => r && r.validData && r.validData.kind === 'bill');
      if (bill) add(`bill:${b.id}`, `${b.name || b.id} bill`, bill.r, bill.k);
    }
  }
  if (phases.impression && phases.impression.fired) add('impression', 'Impression', rowFor(phases.impression), phases.impression.rowKey);
  if (phases.click && phases.click.fired) add('click', 'Click', rowFor(phases.click), phases.click.rowKey);

  entries.sort((a, b) => a.start - b.start);

  // Ordering findings: a header-bidding answer that landed after GAM was called
  // cannot be in that request's targeting.
  const notes = [];
  for (const e of [pb, aps]) {
    if (!e) continue;
    if (e.start > 0) {
      notes.push({ level: 'warn', msg: `${e.label} request started ${fmtMs(e.start)} after the GAM request — its bids cannot be in this request's targeting` });
    } else if (e.end != null && e.end > 0) {
      notes.push({ level: 'warn', msg: `${e.label} response arrived ${fmtMs(e.end)} after the GAM request left — GAM was called before the auction finished` });
    }
  }
  if (isUsbLifecycle(lc)) {
    notes.push({ level: 'info', msg: 'USB capture: request times only (logcat line time); response times are not visible' });
  }
  return { anchorKnown: true, entries, notes };
}
